import React from 'react'
import styled from 'styled-components'
import {
  borderRadius,
  borderRadiusSizes,
  colorConstants,
  marginSizes,
  padding,
  paddingSizes,
  stateColors,
  states,
  stateThemes
} from '@jarvis.oddle/core/theme/default'
import {
  InlineNotification,
  notificationStates
} from '@jarvis.oddle/core/components/InlineNotification'
import IconClose from '@jarvis.oddle/core/components/Icons/IconClose'
import Flex from '@jarvis.oddle/core/components/Flex'
import { P } from '@jarvis.oddle/core/components/Typo'
import Collapsible from '@jarvis.oddle/core/components/Collapsible'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'

import useAppSelector from 'src/hooks/useAppSelector'
import { selectDetailMenuSite, selectMenuMode } from 'src/redux/meta'
import { isObjectEmpty } from 'src/utils/object'
import { selectOrder } from 'src/redux/order'

const TextContainer = styled(InlineNotification).attrs(() => ({
  width: '100%'
}))`
  position: relative;
`

const CloseButtonContainer = styled(Flex)`
  position: absolute;
  top: ${padding[paddingSizes.small]};
  right: ${padding[paddingSizes.small]};
  padding: ${padding[paddingSizes.extraSmall]};
  border-radius: ${borderRadius[borderRadiusSizes.medium]};
  background-color: ${() =>
    stateColors.backgroundColors[states.warning][stateThemes.light]};
  cursor: pointer;
`

export const CloseButton = ({
  onClick,
  className
}: {
  onClick: () => void
  className?: string
}): JSX.Element => (
  <CloseButtonContainer
    data-testid='button.close'
    className={className}
    onClick={onClick}
  >
    <IconClose iconSize='small' fill={colorConstants.black} />
  </CloseButtonContainer>
)

const AnnouncementBar = (): JSX.Element => {
  const { t } = React.useContext(TranslationContext)
  const orderState = useAppSelector(selectOrder)
  const detailMenu = useAppSelector(selectDetailMenuSite)
  const menuMode = useAppSelector(selectMenuMode)
  const [visible, setVisible] = React.useState(true)
  const today = new Date().getTime()

  const onClick = React.useCallback(() => setVisible(false), [])

  if (
    menuMode === 'live' &&
    (isObjectEmpty(detailMenu?.announcementBar) ||
      (orderState?.storeOrder?.data?.taxAndCharges?.length || 0) === 0)
  ) {
    return null
  }

  if (
    menuMode === 'live' &&
    (detailMenu.announcementBar.startDateTime * 1000 > today ||
      today > detailMenu.announcementBar.endDateTime * 1000)
  ) {
    return null
  }

  if (menuMode === 'live') {
    return null
  }

  return (
    <Collapsible show={visible}>
      <TextContainer state={notificationStates.warning}>
        <P
          normalised
          marginRight={marginSizes.small}
          color={colorConstants.black}
        >
          {menuMode === 'preview'
            ? t(
                "Your Oddle Shop is in preview mode, so you won't be able to receive orders until you complete your billing account setup."
              )
            : t(detailMenu?.announcementBar?.content)}
        </P>
        <CloseButton onClick={onClick} />
      </TextContainer>
    </Collapsible>
  )
}

export default AnnouncementBar
